import React, { useRef } from "react";
import { MediaZone } from "./MediaZone";
import { useFitText } from "../lib/useFitText";
import { bgStyle } from "../lib/bgStyle";
import { ClockBadge } from "./ClockBadge";

/**
 * CARDS layout — inspired by clinic-style queue displays:
 *  ┌───────────────────────────┬───────────────┐
 *  │ ATEND. PRIORITÁRIO        │               │
 *  │      [HUGE CODE]          │     MEDIA     │
 *  │      RECEPÇÃO             │               │
 *  ├──────────┬──────────┬─────┴────┬──────────┤
 *  │ NORMAL   │ NORMAL   │ NORMAL   │  CLOCK   │
 *  │  [083]   │  [082]   │  [061]   │ Ter 15:23│
 *  │ Mesa 02  │ Mesa 03  │ Mesa 04  │          │
 *  └──────────┴──────────┴──────────┴──────────┘
 *
 * Uses the same color tokens (passwordBg/historyAccent/etc) so it inherits the
 * theme. Background images / transparency are respected through bgStyle().
 */
export const CardsLayout = ({ state, config, colors, media, flashRef, silentByDefault }) => {
  const sectionRef = useRef(null);
  const priorityRef = useRef(null);

  const current = state.currentTicket;
  const history = (state.history || []).slice(0, 3);
  const nomen = config.nomenclature || "Guichê";

  // Auto-fit for the big priority code
  const priorityCodeRef = useFitText(current?.code || "AGUARDE", {
    max: 600,
    min: 80,
    containerRef: priorityRef,
    maxHeightRatio: 0.65,
    widthRatio: 0.9,
  });

  // Small per-card refs for the history "normal" tickets (always 3 hooks for rules-of-hooks)
  const normalRef0 = useRef(null);
  const normalRef1 = useRef(null);
  const normalRef2 = useRef(null);
  const normalRefs = [normalRef0, normalRef1, normalRef2];
  const codeRef0 = useFitText(history[0]?.code || "", { max: 140, min: 32, containerRef: normalRef0, maxHeightRatio: 0.55, widthRatio: 0.95 });
  const codeRef1 = useFitText(history[1]?.code || "", { max: 140, min: 32, containerRef: normalRef1, maxHeightRatio: 0.55, widthRatio: 0.95 });
  const codeRef2 = useFitText(history[2]?.code || "", { max: 140, min: 32, containerRef: normalRef2, maxHeightRatio: 0.55, widthRatio: 0.95 });
  const normalCodeRefs = [codeRef0, codeRef1, codeRef2];

  const passwordBg = bgStyle(colors.passwordBg, colors.passwordBgOpacity ?? 1, colors.passwordBgImage || "");
  const historyCardBg = bgStyle(
    colors.historyCardBg || "#FFFFFF",
    colors.historyCardBgOpacity ?? 1,
    colors.historyCardBgImage || ""
  );

  return (
    <div
      ref={sectionRef}
      className="relative h-full w-full p-4 md:p-6 flex flex-col gap-4 md:gap-6"
      style={passwordBg}
      data-testid="cards-layout"
    >
      {/* TOP ROW: Priority (smaller, ~40%) + Media (larger, ~60%) */}
      <div className="flex-1 grid grid-cols-1 md:grid-cols-5 gap-4 md:gap-6 min-h-0">
        {/* Priority card */}
        <div
          ref={priorityRef}
          className="md:col-span-2 relative overflow-hidden rounded-2xl shadow-lg flex flex-col p-5 md:p-8 min-h-0"
          style={{
            ...bgStyle(colors.passwordAccent, 0.95, ""),
            color: colors.passwordBg,
          }}
          data-testid="cards-priority"
        >
          <div className="text-sm md:text-base uppercase tracking-[0.3em] font-bold opacity-90 shrink-0">
            Atend. Prioritário
          </div>
          <div ref={flashRef} className="flex-1 flex flex-col items-center justify-center min-h-0 gap-2">
            <div
              ref={priorityCodeRef}
              className="font-heading font-black leading-none tracking-tighter whitespace-nowrap"
              data-testid="cards-priority-code"
            >
              {current?.code || "AGUARDE"}
            </div>
            {current?.desk && (
              <div className="text-2xl md:text-4xl lg:text-5xl uppercase tracking-[0.18em] font-bold opacity-95 mt-2 md:mt-4">
                {nomen} {current.desk}
              </div>
            )}
          </div>
        </div>
        {/* Media — larger area, properly framed */}
        <div
          className="md:col-span-3 relative overflow-hidden rounded-2xl shadow-lg min-h-0 bg-black/5"
          data-testid="cards-media"
        >
          <MediaZone media={media} bgColor={colors.mediaBg} silentByDefault={silentByDefault} />
        </div>
      </div>

      {/* BOTTOM ROW: 3 normal history cards + clock */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 h-[28%] min-h-[120px]">
        {[0, 1, 2].map((i) => {
          const h = history[i];
          return (
            <div
              key={i}
              ref={normalRefs[i]}
              className="relative overflow-hidden rounded-xl shadow-md flex flex-col items-center justify-center p-3 md:p-4 border-2"
              style={{
                ...historyCardBg,
                color: colors.passwordText,
                borderColor: colors.historyAccent + "55",
              }}
              data-testid={`cards-normal-${i}`}
            >
              <div className="text-xs md:text-base lg:text-lg uppercase tracking-[0.25em] font-bold opacity-70 shrink-0">
                Atend. Normal
              </div>
              {h ? (
                <>
                  <div
                    ref={normalCodeRefs[i]}
                    className="font-heading font-black leading-none tracking-tighter whitespace-nowrap"
                    style={{ color: colors.historyAccent }}
                  >
                    {h.code}
                  </div>
                  <div className="text-sm md:text-base lg:text-lg uppercase tracking-[0.2em] opacity-75 font-semibold">
                    {nomen} {h.desk}
                  </div>
                </>
              ) : (
                <div className="text-xl md:text-3xl font-bold opacity-20">—</div>
              )}
            </div>
          );
        })}
        {/* Clock card */}
        <div
          className="relative overflow-hidden rounded-xl shadow-md flex flex-col items-center justify-center p-3 md:p-4"
          style={{
            ...bgStyle(colors.tickerBg, colors.tickerBgOpacity ?? 1, ""),
            color: colors.tickerText,
          }}
          data-testid="cards-clock"
        >
          <div className="text-[10px] md:text-xs uppercase tracking-[0.25em] font-bold opacity-70 shrink-0">
            {config.brandName || "Fila"}
          </div>
          <ClockBadge className="font-heading font-black text-3xl md:text-5xl lg:text-6xl tracking-tighter mt-1" />
        </div>
      </div>
    </div>
  );
};
